export type BlogSection = {
  heading: string;
  paragraphs: string[];
};

export type BlogPost = {
  slug: string;
  title: string;
  seoTitle: string;
  metaDescription: string;
  dateLabel: string;
  datePublished: string;
  excerpt: string;
  image: string;
  imageAlt: string;
  sections: BlogSection[];
};

export const posts: BlogPost[] = [
  {
    slug: "junk-removal-in-lehi",
    title: "Junk Removal in Lehi Before the Snow Sets In",
    seoTitle:
      "Junk Removal Services in Lehi, UT | Scotty's Land Specialist",
    metaDescription:
      "Residential junk removal in Lehi, UT. Scott loads and hauls furniture, brush, and remodel debris from the garage, shed, or yard in one trip. Call or text for a free quote.",
    dateLabel: "November 5, 2026",
    datePublished: "2026-11-05",
    excerpt:
      "The remodel is done, the leaves are down, and the pile in the side yard is still there. Here is how junk removal works in Lehi before snow covers it.",
    image: "/photos/lehi-junk-removal-blog-2026-11-05.webp",
    imageAlt:
      "Old couch, mattress, pallets, and remodel scraps piled on a Lehi driveway in late fall",
    sections: [
      {
        heading: "The pile that waits for spring",
        paragraphs: [
          "In Lehi, a lot of homes have a pile somewhere. It is the couch that came out of the basement, the scraps from a remodel, the brush from trimming the yard, and the pallets nobody wanted to cut up. It sits beside the garage because there is room for it, and then the first snow lands on top of it.",
          "Once snow covers a pile, it usually stays until spring. Frozen brush and wet furniture are heavier and harder to load. November is the easier time to get it gone, while the ground is still dry and the driveway is clear.",
        ],
      },
      {
        heading: "What residential junk removal takes",
        paragraphs: [
          "Junk Removal covers the things a weekly trash can will not take: furniture, mattresses, brush and yard waste, and remodel or construction debris. A mixed pile is normal. Furniture next to brush next to leftover drywall can go on the same haul when it is all one pile.",
          "You do not have to sort it or drag it to the curb first. Scott loads it and hauls it off the property. Clear a path so he can get to it, and say if the pile is behind a gate or mixed in with things you are keeping. If something in the pile is outside what the team takes, they will tell you before anyone drives out.",
        ],
      },
      {
        heading: "Garage, shed, and side yard junk cleanup",
        paragraphs: [
          "Moving junk from the garage to the shed only hides it. The goal is to get the material off the lot, so the garage has room for the car again before winter. Garages, sheds, and the open yard are the same job when everything is leaving.",
          "In Lehi, Traverse Mountain sits on Highland's border, and a lot of the hauling there follows remodels. If you are still tearing out and the debris will keep coming for a day, Dumpster Rental may fit better. The container stays for 24 hours: it is dropped off, you fill it that day, and it is picked up the next day.",
        ],
      },
      {
        heading: "Do the haul before the snow work starts",
        paragraphs: [
          "A clear side yard is also easier to keep open in winter. If you are already lining up the driveway and walkway for the season, our post on [fall cleanup and snow removal in Highland](/blog/fall-cleanup-and-snow-removal-in-highland) covers what to book before the snow packs things down.",
        ],
      },
      {
        heading: "How to book junk removal services",
        paragraphs: [
          "Call or text the team at (801) 400-3346. Business hours are Monday through Friday, 11:00 AM to 7:00 PM, and texts come in day and night. Tell them what is in the pile and where it is sitting, and someone on the team gets back to you with a free quote and a pickup time.",
          "Photos make the quote faster: the pile, how close a truck can get, and anything around it that stays. Scott comes from Highland, loads the pile, and takes it away in one trip.",
        ],
      },
    ],
  },
  {
    slug: "fall-cleanup-and-snow-removal-in-highland",
    title: "Fall Cleanup and Snow Removal in Highland",
    seoTitle:
      "Fall Cleanup and Snow Removal in Highland, UT | Scotty's Land Specialist",
    metaDescription:
      "Fall cleanup in Highland runs through mid-December, and into January when the snow holds off. Snow removal covers the driveway the city plow leaves behind.",
    dateLabel: "November 4, 2026",
    datePublished: "2026-11-04",
    excerpt:
      "Leaves are still on the ground, and snow is already on the mountains. Here is what to book now so a Highland lot is clear before the snow packs it down.",
    image: "/photos/highland-fall-snow-blog.jpg",
    imageAlt: "Highland yard with fallen leaves and snow on the mountains",
    sections: [
      {
        heading: "Both jobs are open right now",
        paragraphs: [
          "Fall cleanup in Highland runs through the middle of December, and into January when the snow holds off. Snow is already on the mountains, so both are booking at the same time. Waiting until the first real storm usually means the leaves are frozen into the yard and the driveway is the thing that has to be cleared first.",
          "A large Highland lot collects more than a city street does. Leaves, garden beds, and the oak brush that comes off the east side toward American Fork Canyon sit until something hauls them. The regular trash can will not take that pile.",
        ],
      },
      {
        heading: "What fall cleanup actually clears",
        paragraphs: [
          "Fall Cleanup takes the leaves, garden beds, and yard debris off the lot before the snow packs them down. The point is a yard you can use again, not a thin pass that leaves the corners full. If the pile is oak brush or a remodel cleanout, that is a haul, not a bag for the curb.",
          "Dumpster Rental is a separate job when the debris has to sit in one place while you load it. The container stays for 24 hours. Scott drops it off, you fill it that day, and he picks it up the next day.",
        ],
      },
      {
        heading: "The city plow stops at the street",
        paragraphs: [
          "Highland City plows the streets, not the driveway. The blade also pushes a ridge of snow across the opening, so the street can be clear while the drive is blocked. Scotty's does not plow city streets. Snow Removal is the driveway, the walkway, and the access you actually use.",
          "If a storm is already on the way, call or text with the address and whether you need the drive, the walk, or both. The team will say what they can fit in.",
        ],
      },
      {
        heading: "How to get on the schedule",
        paragraphs: [
          "Call or text the team at (801) 400-3346. Business hours are Monday through Friday, 11:00 AM to 7:00 PM, and texts come in day and night. Someone on the team gets back to you about the lot and a free quote before any work starts.",
          "A few photos help: the yard if you want Fall Cleanup, the drive if you want Snow Removal, and the pile if it needs a haul. Scott comes from Highland and does the work. You do not have to guess which service fits.",
        ],
      },
    ],
  },
];

export function getPost(slug: string): BlogPost | undefined {
  return posts.find((post) => post.slug === slug);
}
